import React, { useEffect, useState } from 'react';
import { Heading, Icon, Surface, Text } from '@/components/ui';

// Public palette keys for this widget — exposed so the form/selector can
// iterate them without importing Surface internals. Maps 1:1 onto the
// Surface tone prop, which owns the actual surface chrome (border,
// background, foreground, ring). Everything inside the widget — Heading,
// Text, Icon — uses tone="inherit" and follows the surface's foreground
// colour automatically. Product code chooses one of these keys and
// nothing else.
//
// `'simple'` is accepted at read time for stored widgets that predate
// the variant→tone rename; it resolves to `neutral` (visually identical).
export const VALUE_CARD_PALETTES = Object.freeze(['primary', 'secondary', 'neutral']);

const LEGACY_PALETTE_TO_TONE = {
    simple: 'neutral'
};

const VALUE_CARD_ICON_MAP = {
    actuators: 'actuators',
    battery: 'battery',
    bolt: 'bolt',
    carbonDioxide: 'carbonDioxide',
    change: 'change',
    clock: 'clock',
    cooling: 'cooling',
    data_center: 'dataCenter',
    density: 'density',
    flask: 'flask',
    frequency: 'frequency',
    gas: 'gas',
    global: 'global',
    heating: 'heating',
    humidity: 'humidity',
    info: 'info',
    magnet: 'magnet',
    oil: 'oil',
    pressure: 'pressure',
    sun: 'sun',
    users: 'users',
    value: 'value',
    water: 'water'
};

const datesInterval = function (period, timezone) {
    let from = new Date();
    let to = new Date();

    switch (period) {
        case 'last_value':
            from.setHours(from.getHours() - 24);
            break;
        case 'wtd':
            from.setDate(from.getDate() - from.getDay());
            break;
        case 'mtd':
            from.setDate(1);
            break;
        case 'ytd':
            from.setMonth(0);
            from.setDate(1);
            break;
    }

    return {
        from: moment.tz(from, timezone).format('YYYY-MM-DD HH:mm:ss'),
        to: moment.tz(to, timezone).format('YYYY-MM-DD HH:mm:ss')
    };
};

export default function ValueCardWidget ({ widget }) {
    // Small widgets (h ≤ 3 OR w ≤ 2) get a tighter font multiplier
    // below so the value still fits inside the bounded card. Same
    // threshold used by the `baseDimension` clamp further down.
    const isSmallWidget = widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2;

    const
            [displayValue, setDisplayValue] = useState(null),
            [timestamp, setTimestamp] = useState(null),
            [value, setValue] = useState(null);

    const valueRef = React.useRef(null);

    const
            model = WiseMetering.indicators.get(widget.data.indicatorId),
            ratio = widget.data.period === 'last_value' ? 900 : 0;

    const
            rawPalette = widget.graphical?.palette,
            normalizedPalette = LEGACY_PALETTE_TO_TONE[rawPalette] ?? rawPalette,
            widgetTone = VALUE_CARD_PALETTES.includes(normalizedPalette) ? normalizedPalette : 'primary',
            icon = VALUE_CARD_ICON_MAP[WiseMetering.icons.getIconFromKind(model?.get('kind_slug'))] || 'info',
            unit = model?.get('unit') || '';

    useEffect(() => {
        if (!model) {
            setValue('--');
            return;
        }

        const fetchValue = () => {
            if (widget.data.period === 'last_value') {
                model.fetchIndicatorData('/last_value', {}).then((response) => {
                    const scale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;
                    if (response) {
                        const val = response.v;
                        const ts = response.t;

                        if (val !== null && val !== undefined) {
                            setValue(val);
                            setTimestamp(ts);
                            setDisplayValue(formatValue(val, unit, widget.data.decimals, scale));
                            return;
                        }
                    }
                    setDisplayValue('--');
                    setValue(null);
                    setTimestamp(null);
                }).catch(() => {
                    setDisplayValue('--');
                    setValue(null);
                    setTimestamp(null);
                });
                return;
            }

            const period = datesInterval(widget.data.period, model.timezone());

            const params = {
                from: period.from,
                to: period.to,
                rate: ratio
            };

            model.getRealtimeData(params).then((response) => {
                const scale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;

                if (Array.isArray(response.data) && response.data.length > 0) {
                    const cleaned = response.data.filter(o => o.v !== null);

                    if (cleaned.length === 0) {
                        setDisplayValue('--');
                        return;
                    }

                    const latestDataPoint = cleaned[cleaned.length - 1].v;

                    setValue(latestDataPoint);
                    setDisplayValue(formatValue(latestDataPoint, unit, widget.data.decimals, scale));
                } else if (response?.data !== undefined && response?.data !== null) {
                    setValue(response?.data);
                    setDisplayValue(formatValue(response?.data, unit, widget.data.decimals, scale));
                } else {
                    setDisplayValue('--');
                }
            }).catch(() => setDisplayValue('--'));
        };

        fetchValue();

        const interval = setInterval(fetchValue, 60000);

        return () => clearInterval(interval);
    }, [model, ratio, unit, widget.data.decimals, widget.data.indicatorId, widget.data.period, widget.data.unitScale]);

    useEffect(() => {
        const scale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;

        setDisplayValue(formatValue(value, unit, widget.data.decimals, scale));
    }, [unit, value, widget.data.decimals, widget.data.unitScale]);

    useEffect(() => {
        const widgetElement = document.getElementById(widget.id);
        const characterCount = Math.max(displayValue ? displayValue.length : 9, 5);

        let baseDimension = widgetElement?.offsetWidth || 100;

        if (widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2) {
            const widgetHeightPx = widgetElement?.offsetHeight || 100;
            baseDimension = Math.min(baseDimension, widgetHeightPx);
        }

        const maxFontSize = 80;
        const minFontSize = 12;

        const multiplier = isSmallWidget ? 1.1 : 1.35;

        const calculatedFontSize = Math.min(
                maxFontSize,
                Math.max(minFontSize, (baseDimension / characterCount) * multiplier)
        );

        if (valueRef.current) {
            valueRef.current.style.fontSize = `${calculatedFontSize}px`;
        }
    }, [displayValue, widget.graphical.size.h, widget.graphical.size.w, widget.id]);

    return (
            <Surface fullHeight id={widget.id} tone={widgetTone}>
                <div className="tw-flex tw-h-full tw-flex-col tw-justify-between tw-gap-1">
                    {/* Header (top) */}
                    <div>
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <Heading as="span" tone="inherit" variant="title-sm">{widget.title}</Heading>
                            <Icon icon={icon} size="medium" tone="inherit"/>
                        </div>
                        {/* Value + Legend (bottom, right-aligned) */}
                        <div className="tw-flex tw-flex-col tw-items-end tw-pb-2">
                            <div ref={valueRef}>
                                <Heading as="span" tone="inherit" variant="display">
                                    {!displayValue ? 'Loading…' : displayValue}
                                </Heading>
                            </div>
                            <div className="tw-mt-1">
                                <Heading as="span" tone="inherit" variant="title">
                                    {widget.legend || ''}
                                </Heading>
                            </div>
                            {/* Last-value timestamp — only shown when the
                                user picked the "last value" period AND a
                                timestamp came back from the indicator
                                fetch. Lives below the legend, in the same
                                right-aligned column. */}
                            {widget.data.period === 'last_value' && timestamp ? (
                                <Text tone="inherit" variant="caption">
                                    {moment(typeof timestamp === 'number' ? timestamp * 1000 : timestamp)
                                            .tz(model?.timezone() || moment.tz.guess())
                                            .format('YYYY/MM/DD HH:mm')}
                                </Text>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Surface>
    );
}
